import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Heart, Gift, Users, Building2, LogOut, ChevronRight, Copy, Globe, Map as MapIcon, MapPin, UsersRound, MessagesSquare, Plane, ShieldCheck, FileText, Compass } from "lucide-react";
import NotificationsCard from "../components/NotificationsCard";
import MyAccount from "./MyAccount";

export default function More() {
  const { user, org, config, logout, isTeam } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const tripSetup = !!(config?.setup_complete || config?.trip_name || config?.start_date);

  if (user?.role === "partner") return <MyAccount />;

  const items = [
    { icon: MapPin, label: "Trip", to: "/app/itinerary", show: true },
    { icon: MapIcon, label: "Build mission trip", to: "/build-trip", show: isAdmin && !tripSetup },
    { icon: UsersRound, label: "Our Team", to: "/app/team", show: true },
    { icon: FileText, label: "Documents", to: "/app/itinerary?tab=documents", show: isAdmin },
    { icon: MessagesSquare, label: "Team Channel", to: "/app/team-channel", show: isTeam },
    { icon: Heart, label: "Your Impact", to: "/app/impact", show: user?.role === "partner" },
    { icon: Gift, label: "Gifts", to: "/app/gifts", show: isAdmin },
    { icon: Users, label: "Partners", to: "/app/partners", show: isAdmin },
    { icon: Compass, label: "Journeys", to: "/app/trips", show: isAdmin },
    { icon: Plane, label: "Trip settings", to: "/app/trip-settings", show: isAdmin },
    { icon: Building2, label: "Organization", to: "/app/settings", show: isAdmin },
  ].filter((i) => i.show);

  const copyCode = () => { navigator.clipboard.writeText(org?.join_code || ""); toast.success("Join code copied"); };

  return (
    <div>
      <div className="bg-white rounded-2xl p-5 shadow-[0_4px_24px_rgba(44,44,62,0.04)] flex items-center gap-4 mb-5">
        <div className="h-14 w-14 rounded-2xl accent-bg flex items-center justify-center text-white font-heading font-bold text-2xl">{(user?.name || "?")[0]}</div>
        <div>
          <div className="font-heading font-bold text-xl text-[#2C2C3E]">{user?.name}</div>
          <div className="text-sm text-muted-foreground">{user?.email}</div>
          <div className="flex items-center gap-1.5 mt-1">
            <span className="text-xs inline-block accent-text font-semibold uppercase tracking-wide">Signed in · {user?.role}</span>
            {user?.is_owner && <span data-testid="owner-badge" className="text-[10px] font-bold uppercase tracking-wide bg-[var(--brand-navy)] text-white rounded-full px-2 py-0.5">Owner</span>}
          </div>
        </div>
      </div>

      <NotificationsCard />

      {(org?.mission_statement || org?.website) && (
        <div className="bg-white rounded-2xl p-5 shadow-[0_4px_24px_rgba(44,44,62,0.04)] mb-5">
          <div className="font-heading font-bold text-lg text-[var(--brand-navy)]">{org?.org_name}</div>
          {org?.mission_statement && <p className="text-sm text-[#2C2C3E]/80 mt-1">{org.mission_statement}</p>}
          {org?.website && (
            <a data-testid="website-link" href={org.website.startsWith("http") ? org.website : `https://${org.website}`} target="_blank" rel="noreferrer"
              className="mt-3 inline-flex items-center gap-2 text-sm font-medium accent-text hover:opacity-80">
              <Globe className="h-4 w-4" /> Visit our website
            </a>
          )}
          <div className="mt-2 flex flex-col gap-0.5 text-xs text-muted-foreground">
            {org?.email && <span>{org.email}</span>}
            {org?.phone && <span>{org.phone}</span>}
          </div>
        </div>
      )}

      {isAdmin && (
        <button onClick={copyCode} data-testid="copy-join-code-btn" className="w-full bg-[var(--brand-navy)]/5 rounded-2xl p-4 flex items-center justify-between mb-5 hover:bg-[var(--brand-navy)]/10 transition-colors">
          <div className="text-left">
            <div className="text-xs text-muted-foreground">Partner join code</div>
            <div className="font-heading font-bold text-2xl tracking-widest text-[var(--brand-navy)]">{org?.join_code}</div>
          </div>
          <Copy className="h-5 w-5 text-[var(--brand-navy)]" />
        </button>
      )}

      <div className="bg-white rounded-2xl overflow-hidden shadow-[0_4px_24px_rgba(44,44,62,0.04)]">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <button key={it.to} data-testid={`more-${it.label.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => navigate(it.to)}
              className="w-full flex items-center gap-3 px-5 py-4 border-b last:border-0 hover:bg-muted/40 transition-colors">
              <Icon className="h-5 w-5 accent-text" />
              <span className="flex-1 text-left font-medium text-[#2C2C3E]">{it.label}</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </button>
          );
        })}
      </div>

      <button data-testid="logout-btn" onClick={async () => { await logout(); const s = localStorage.getItem("mc_org_slug"); navigate(s ? `/${s}` : "/"); }}
        className="w-full mt-5 flex items-center justify-center gap-2 rounded-full border border-destructive/30 text-destructive py-3 hover:bg-destructive/5 transition-colors">
        <LogOut className="h-4 w-4" /> Sign out
      </button>
    </div>
  );
}
